import { useEffect, useState } from "react";
import { BREACHED_PASSWORD_MESSAGE } from "@/lib/authErrors";

/**
 * Checks a new password against the list of passwords leaked in data breaches
 * (Have I Been Pwned), the same list the sign up server already refuses.
 * Checking it while the person types means the checklist can say so before
 * they tap Create account, instead of a surprise error after every box is
 * ticked.
 *
 * Privacy: the password never leaves the device. Only the first 5 characters
 * of its SHA-1 hash are sent, the service answers with every leaked hash that
 * starts with them (hundreds, padded so the size gives nothing away), and the
 * match happens here.
 */

const RANGE_URL = "https://api.pwnedpasswords.com/range/";
const TIMEOUT_MS = 4000;

async function sha1Hex(text: string): Promise<string> {
  const bytes = new TextEncoder().encode(text);
  const digest = await crypto.subtle.digest("SHA-1", bytes);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .toUpperCase();
}

/** True if leaked, false if not, null if the list could not be reached. */
export async function isPasswordLeaked(password: string, signal?: AbortSignal): Promise<boolean | null> {
  try {
    const hash = await sha1Hex(password);
    const prefix = hash.slice(0, 5);
    const suffix = hash.slice(5);
    const timeout = new AbortController();
    const timer = setTimeout(() => timeout.abort(), TIMEOUT_MS);
    const onAbort = () => timeout.abort();
    signal?.addEventListener("abort", onAbort);
    try {
      const res = await fetch(RANGE_URL + prefix, {
        headers: { "Add-Padding": "true" },
        signal: timeout.signal,
      });
      if (!res.ok) return null;
      const body = await res.text();
      for (const line of body.split("\n")) {
        const [lineSuffix, count] = line.trim().split(":");
        // Padding rows carry a count of 0 and are not real leaks.
        if (lineSuffix === suffix && Number(count) > 0) return true;
      }
      return false;
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener("abort", onAbort);
    }
  } catch {
    return null;
  }
}

export type LeakCheckState =
  /** The other rules are not all met yet, so nothing is checked. */
  | "waiting"
  | "checking"
  | "clear"
  | "leaked"
  /** The list could not be reached; the server still checks on submit. */
  | "unavailable";

const DEBOUNCE_MS = 450;
// Answers for this page visit, so the checklist and the submit check never
// ask twice for the same password. Unreachable answers are not kept.
const results = new Map<string, boolean>();

async function checkOnce(password: string, signal?: AbortSignal): Promise<boolean | null> {
  const known = results.get(password);
  if (known !== undefined) return known;
  const leaked = await isPasswordLeaked(password, signal);
  if (leaked !== null && !signal?.aborted) results.set(password, leaked);
  return leaked;
}

/**
 * For a screen's submit: the friendly message if the password is leaked,
 * otherwise null. If the list cannot be reached it returns null and the
 * server's own check still applies.
 */
export async function passwordLeakError(password: string): Promise<string | null> {
  return (await checkOnce(password)) === true ? BREACHED_PASSWORD_MESSAGE : null;
}

/**
 * Leak status of `password`, checked once the person pauses typing and only
 * when `ready` (every other rule met), so half typed passwords are never sent.
 */
export function usePasswordLeakCheck(password: string, ready: boolean): LeakCheckState {
  const [state, setState] = useState<LeakCheckState>("waiting");

  useEffect(() => {
    if (!ready || !password) {
      setState("waiting");
      return;
    }
    const cached = results.get(password);
    if (cached !== undefined) {
      setState(cached ? "leaked" : "clear");
      return;
    }
    setState("checking");
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      const leaked = await checkOnce(password, controller.signal);
      if (controller.signal.aborted) return;
      setState(leaked === null ? "unavailable" : leaked ? "leaked" : "clear");
    }, DEBOUNCE_MS);
    return () => {
      controller.abort();
      clearTimeout(timer);
    };
  }, [password, ready]);

  return state;
}

/** Checklist label for each state, shared by every screen that sets a password. */
export const LEAK_CHECK_LABEL: Record<LeakCheckState, string> = {
  waiting: "Not a leaked password",
  checking: "Checking it's not a leaked password",
  clear: "Not a leaked password",
  leaked: "This password was leaked online. Try another one.",
  unavailable: "Not a leaked password",
};

/** Whether the form may submit. Unreachable list: let the server decide. */
export function leakCheckAllowsSubmit(state: LeakCheckState): boolean {
  return state === "clear" || state === "unavailable";
}
